import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { AppConfig } from '®shared/config';
import {
  EditMenuVisibility,
  UserPermissions,
  getEditMenuVisibility,
} from '®shared/permissions';
import { ReadingListDetails } from '®shared/reading-list';
import {
  ArchiveListShowModalAction,
  DeleteListShowModalAction,
} from '®shared/reading-list/edit-modals';
import { NodesEditorShowModalAction } from '®shared/reading-list/hierarchy/editor';
import { ListDetailsEditorShowModalAction } from '®shared/reading-list/list-details/editor';
import { ListOwnersEditorShowModalAction } from '®shared/reading-list/list-owners';
import { RequestReviewShowModalAction } from '®shared/reviews/request-review';
import { ReviewHistoryShowModalAction } from '®shared/reviews/review-history';
import { UriService } from '®shared/url-utils';

import { State } from '../../../ngrx';
import { selectQueueIsEmpty } from '../../container-structure/edit/queue';
import { EditableList } from '../models';

@Component({
  selector: 'rl-edit-menu',
  templateUrl: './edit-menu.component.html',
})
export class EditMenuComponent implements OnChanges, OnInit, OnDestroy {
  @Input() list: ReadingListDetails;
  @Input() editableList: EditableList;
  @Input() isEdit: boolean;
  @Input() currentUserPermissions: UserPermissions;
  @Output() showArchiveListModal =
    new EventEmitter<ArchiveListShowModalAction>();
  @Output() showDeleteListModal = new EventEmitter<DeleteListShowModalAction>();
  @Output() showEditListDetailsModal =
    new EventEmitter<ListDetailsEditorShowModalAction>();
  @Output() showAssignListOwnersModal =
    new EventEmitter<ListOwnersEditorShowModalAction>();
  @Output()
  showRequestReviewModal = new EventEmitter<RequestReviewShowModalAction>();
  @Output()
  showReviewHistoryModal = new EventEmitter<ReviewHistoryShowModalAction>();
  @Output()
  showStudentNumbersAndHierarchyModal =
    new EventEmitter<NodesEditorShowModalAction>();

  baseUri: string;
  conceptUri: string;
  copyListUrl: string;
  editMenuVisibility: EditMenuVisibility;
  inviteListPublishersUrl: string;
  disableEtagInvalidatingActions = false;
  isCourseFlowTenancy = this.appConfig.tenant.featureFlags.isCourseFlowTenancy;

  private queueSubscription: Subscription;

  constructor(
    private store: Store<State>,
    private uriService: UriService,
    private appConfig: AppConfig,
  ) {}

  ngOnInit() {
    this.queueSubscription = this.store
      .select(selectQueueIsEmpty)
      .subscribe(queueIsEmpty => {
        this.disableEtagInvalidatingActions = !queueIsEmpty;
      });
  }

  ngOnDestroy() {
    if (this.queueSubscription) {
      this.queueSubscription.unsubscribe();
    }
  }

  ngOnChanges() {
    this.copyListUrl = this.uriService.constructCopyListUrl(
      this.list.id,
      this.list.is_draft,
    );

    this.inviteListPublishersUrl = this.uriService.constructInvitePublishersUrl(
      this.list.id,
    );

    if (this.currentUserPermissions) {
      this.editMenuVisibility = getEditMenuVisibility(
        this.list,
        this.editableList,
        this.currentUserPermissions,
      );
    }
  }

  openEditListDetailsModal() {
    if (!this.disableEtagInvalidatingActions) {
      this.showEditListDetailsModal.emit(
        new ListDetailsEditorShowModalAction({
          listId: this.list.id,
        }),
      );
    }
  }

  openArchiveListModal() {
    if (!this.disableEtagInvalidatingActions) {
      this.showArchiveListModal.emit(
        new ArchiveListShowModalAction({
          listId: this.list.id,
        }),
      );
    }
  }

  openDeleteListModal() {
    if (!this.disableEtagInvalidatingActions) {
      this.showDeleteListModal.emit(
        new DeleteListShowModalAction({
          listId: this.list.id,
        }),
      );
    }
  }

  assignListOwners() {
    if (!this.disableEtagInvalidatingActions) {
      this.showAssignListOwnersModal.emit(
        new ListOwnersEditorShowModalAction({
          listId: this.list.id,
        }),
      );
    }
  }

  editStudentNumbersAndHierarchy() {
    if (!this.disableEtagInvalidatingActions) {
      this.showStudentNumbersAndHierarchyModal.emit(
        new NodesEditorShowModalAction({
          isDraft: this.list.is_draft,
          listId: this.list.id,
        }),
      );
    }
  }

  requestReview() {
    if (!this.disableEtagInvalidatingActions) {
      this.showRequestReviewModal.emit(
        new RequestReviewShowModalAction({ listId: this.list.id }),
      );
    }
  }

  reviewHistory() {
    this.showReviewHistoryModal.emit(
      new ReviewHistoryShowModalAction({ listId: this.list.id }),
    );
  }
}
